import { useCallback, useState, useEffect } from 'react';

/**
 * Tarea #11 — Selector de page-size configurable, persistido POR TABLA.
 *
 * Devuelve `{ pageSize, setPageSize, availableOptions }`.
 *
 * # Por qué storage POR TABLA
 *
 * El cursor de Redux ES por tabla (`cursorTables[tableId].take`). Si el
 * selector leyera de una key global compartida entre todas las tablas, al
 * navegar a una tabla por primera vez en la sesión el cursor estaría en
 * `takeBase=25` (initial state) pero el selector mostraría la preferencia
 * global (e.g. "100") → fetch trae 25 filas, UI dice 100. Mismatch UX
 * reproducible (ver `docs/superpowers/plans/2026-05-12-page-size-per-table.md`).
 *
 * Solución: cada tabla persiste en `rois-page-size:${tableId}`. El selector
 * y el cursor de esa tabla están alineados sin necesidad de mount-sync.
 *
 * # API
 *
 *   const { pageSize, setPageSize, availableOptions } = usePersistedPageSize(tableId);
 *
 * `tableId` es **requerido** — el slug del cursor en Redux
 * (e.g. `tableIds.ACCIDENTS = 'accidents'`). Hacerlo requerido evita el bug de
 * pasar `undefined` accidentalmente y volver al comportamiento global compartido.
 *
 * Defaults:
 * - Sin valor previo en localStorage → `25` (alineado con `takeBase`).
 * - Valor inválido en localStorage → cae al default.
 *
 * Cross-tab sync: cada hook escucha SOLO la key de su tableId. Cambios en otras
 * tablas no propagan (correcto: son preferencias independientes).
 */

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number];

/**
 * Prefijo común de las storage keys. Cada tabla usa
 * `${PAGE_SIZE_STORAGE_PREFIX}:${tableId}`. Exportado para que el logout
 * (`UserMenu/action.ts`) pueda hacer prefix-match y limpiar todas las
 * preferencias persistidas sin acoplarse a la lista de tableIds.
 */
export const PAGE_SIZE_STORAGE_PREFIX = 'rois-page-size';

const DESKTOP_DEFAULT: PageSizeOption = 25;

const storageKeyFor = (tableId: string): string =>
  `${PAGE_SIZE_STORAGE_PREFIX}:${tableId}`;

const parseValid = (raw: string | null): PageSizeOption | null => {
  if (!raw) return null;
  const parsed = Number.parseInt(raw, 10);
  return PAGE_SIZE_OPTIONS.includes(parsed as PageSizeOption)
    ? (parsed as PageSizeOption)
    : null;
};

const readStoredPageSize = (tableId: string): PageSizeOption => {
  if (typeof window === 'undefined') return DESKTOP_DEFAULT;
  try {
    return (
      parseValid(window.localStorage.getItem(storageKeyFor(tableId))) ??
      DESKTOP_DEFAULT
    );
  } catch {
    return DESKTOP_DEFAULT;
  }
};

const writeStoredPageSize = (value: PageSizeOption, tableId: string): void => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKeyFor(tableId), String(value));
  } catch {
    /* localStorage llena o deshabilitada — el cambio queda en memoria */
  }
};

export interface UsePersistedPageSizeReturn {
  pageSize: number;
  setPageSize: (next: PageSizeOption) => void;
  availableOptions: readonly PageSizeOption[];
}

export const usePersistedPageSize = (
  tableId: string,
): UsePersistedPageSizeReturn => {
  const [stored, setStored] = useState<PageSizeOption>(() =>
    readStoredPageSize(tableId),
  );

  // Re-leer si tableId cambia (caso raro pero posible: un mismo mount
  // se reutiliza para distintas tablas dinámicas).
  useEffect(() => {
    setStored(readStoredPageSize(tableId));
  }, [tableId]);

  // Cross-tab sync: si otra pestaña cambia la preferencia de ESTA tabla,
  // esta pestaña lo recoge. Solo escuchamos la key específica — cambios
  // en otras tablas son independientes.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const targetKey = storageKeyFor(tableId);
    const onStorage = (e: StorageEvent) => {
      if (e.key === targetKey) {
        setStored(readStoredPageSize(tableId));
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [tableId]);

  const setPageSize = useCallback(
    (next: PageSizeOption) => {
      writeStoredPageSize(next, tableId);
      setStored(next);
    },
    [tableId],
  );

  return {
    pageSize: stored,
    setPageSize,
    availableOptions: PAGE_SIZE_OPTIONS,
  };
};
